import { X } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import { Button } from '@/components/ui/button';
import { ADMIN_NAV, isNavItemActive } from '@/lib/admin-nav';
import { cn } from '@/lib/utils';

/**
 * The admin sidebar. 224px fixed on desktop, a slide-over below 1024px.
 *
 * One component for both, rather than two that drift: the same markup is
 * positioned differently by the classes the layout passes in. The groups and
 * their order come from `@/lib/admin-nav`, and which item is lit comes from
 * `isNavItemActive` there — that is defect P4 and it has its own test.
 */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();

  return (
    <nav aria-label="Admin sections" className="flex h-full flex-col overflow-y-auto px-3 py-4">
      <div className="mb-4 flex items-center justify-between px-2 lg:hidden">
        <span className="text-h3 text-bark-700">Honeycomb</span>
        <Button variant="ghost" size="icon" aria-label="Close menu" onClick={onNavigate}>
          <X aria-hidden="true" />
        </Button>
      </div>

      {ADMIN_NAV.map((group) => (
        <div key={group.label} className="mb-5">
          {/* 11px, 600, 0.06em, uppercase, ink-500 — design-system.md §6. */}
          <p className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
            {group.label}
          </p>

          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const active = isNavItemActive(item.to, pathname);
              const Icon = item.icon;

              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onNavigate}
                    // `page`, not `true` — this is the current page rather than
                    // a toggled control, and screen readers say so differently.
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex min-h-touch items-center gap-2.5 rounded-md px-2 text-label',
                      active
                        ? // ink-900 on honey-400 is 14.75:1. Never honey as text.
                          'bg-honey-400 text-ink-900'
                        : 'text-ink-700 hover:bg-honey-50',
                    )}
                  >
                    <Icon aria-hidden="true" className="size-[18px] shrink-0" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
